import { useCallback, useEffect, useRef, useState } from "react";
import { dayLabel, istHMS, useLive, useTick } from "../lib/time";
import { api } from "../lib/api";
import { ChangePasswordSheet } from "./PasswordSheet";
import { Toasts } from "./Toasts";
import { useEscape } from "./bits";
import { readTheme, saveTheme } from "../lib/theme";

export const NAV = [
  { id: "myday", label: "My day", show: (c) => c.ownList },
  { id: "floor", label: "Floor", show: () => true },
  { id: "plan", label: "Plan", show: (c) => c.plan },
  { id: "clients", label: "Clients", show: (c) => c.clientHours },
  { id: "export", label: "Export", show: (c) => c.export },
  { id: "team", label: "Team", show: (c) => c.people || c.offboard },
];

const ROLE_BLURB = {
  maker: "Runs their own list. Cannot move cards.",
  lead: "Runs their own list and rearranges their team's cards. Estimates and new work stay with the planners.",
  planner: "Moves cards, builds tomorrow, adds clients and work, sees client hours.",
  accounts: "Sees client hours, adds clients, and adds work to the request tray for planners to place. Cannot move cards or plan tomorrow.",
  management: "Plans tomorrow, moves cards, sees client hours, and removes someone who leaves CK. Excel export and IDs are owner access.",
};
const PRESENCE = (t) => /^(timer|break|autostop|leave|person|day|calendar|segment)\./.test(t) || t === "autostop";
const initials = (n) => n.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

function DutyClock({ today }) {
  const t = useTick(1000);
  return (
    <div className="duty" data-testid="duty-clock">
      <span className="duty-time num">{istHMS(t)} <small>IST</small></span>
      <span className="duty-sub">{dayLabel(today)}</span>
    </div>
  );
}

function LiveSummary({ today }) {
  const [c, setC] = useState(null);
  const load = useCallback(async () => {
    const rs = await Promise.all(["design", "video"].map((t) => api(`/floor?date=${today}&team=${t}`)));
    const rows = rs.filter((r) => r.ok && !r.data.hidden).flatMap((r) => r.data.rows);
    const n = (k) => rows.filter((r) => !r.away && r.presence?.kind === k).length;
    setC({ live: n("running"), brk: n("break"), idle: n("idle"), away: rows.filter((r) => r.away && !r.away.roster).length });
  }, [today]);
  useEffect(() => { load(); }, [load]);
  useLive(load, PRESENCE);
  if (!c) return null;
  return (
    <div className="lsum" data-testid="live-summary" aria-label="Floor right now">
      <b className="lsum-live" data-testid="live-count"><i aria-hidden="true" />{c.live} live</b>
      <span data-testid="break-count">{c.brk} on break</span><span data-testid="idle-count">{c.idle} idle</span>
      {c.away > 0 && <b className="t-warn" data-testid="away-count">{c.away} on leave</b>}
    </div>
  );
}

function ThemeToggle() {
  const [v, setV] = useState(readTheme);
  return (
    <div role="group" aria-labelledby="theme-lbl">
      <div id="theme-lbl" className="lbl">Theme</div>
      <div className="theme-row">
        {[["auto", "Auto"], ["light", "Light"], ["dark", "Dark"]].map(([k, l]) => (
          <button key={k} aria-pressed={v === k} className={`theme-btn ${v === k ? "on" : ""}`} onClick={() => { saveTheme(k); setV(k); }} data-testid={`theme-${k}`}>{l}</button>
        ))}
      </div>
    </div>
  );
}

function AccountMenu({ me, onClose, onChangePw, onSignOut }) {
  const ref = useRef(null);
  useEscape(() => { onClose(); document.getElementById("acct-btn")?.focus(); });
  useEffect(() => {
    const down = (e) => { if (!ref.current?.contains(e.target) && !e.target.closest("#acct-btn")) onClose(); };
    const out = (e) => { if (e.relatedTarget && !ref.current?.contains(e.relatedTarget) && !e.relatedTarget.closest("#acct-btn")) onClose(); };
    const el = ref.current;
    document.addEventListener("pointerdown", down);
    el?.addEventListener("focusout", out);
    return () => { document.removeEventListener("pointerdown", down); el?.removeEventListener("focusout", out); };
  }, [onClose]);
  const u = me.user;
  return (
    <div id="acct-menu" className="acct-menu" ref={ref} data-no-shortcuts data-testid="account-menu">
      <div className="acct-who"><span className="avatar lg" aria-hidden="true">{initials(u.name)}</span>
        <div><b data-testid="account-name">{u.name}</b><div className="hint0">{u.designation}</div><span className="tag">{u.role}</span></div></div>
      <p className="acct-blurb">{ROLE_BLURB[u.role]}{me.caps.export ? " You have owner access: Excel export, IDs and the daily list." : ""}</p>
      <button className="acct-row" onClick={onChangePw} data-testid="change-password-open">Change password <span aria-hidden="true">⚿</span></button>
      <ThemeToggle />
      <button className="acct-row" onClick={onSignOut} data-testid="signout-button">Sign out <span aria-hidden="true">→</span></button>
    </div>
  );
}

export function Shell({ me, route, setRoute, onSignOut, children }) {
  const tabs = NAV.filter((n) => n.show(me.caps));
  const [menu, setMenu] = useState(false);
  const [pw, setPw] = useState(false);
  return (
    <div className="shell" data-testid="app-shell">
      <header className="topbar">
        <button className="home-btn" aria-label="Home" onClick={() => setRoute(tabs[0].id)} data-testid="home-button">
          <img src="/logo-160.png" alt="" className="top-logo" /><span className="tiffin">TIFFIN</span></button>
        <nav className="tabs" aria-label="Main" data-testid="main-nav">
          {tabs.map((t) => (
            <button key={t.id} className={`tab ${route === t.id ? "on" : ""}`} aria-current={route === t.id ? "page" : undefined}
              onClick={() => setRoute(t.id)} data-testid={`nav-${t.id}`}>{t.label}</button>
          ))}
        </nav>
        <div className="top-right">
          <LiveSummary today={me.today} />
          <DutyClock today={me.today} />
          <div className="acct">
            <button id="acct-btn" className="acct-btn" aria-expanded={menu} aria-controls="acct-menu" aria-label={`${me.user.short}, account settings`}
              onClick={() => setMenu(!menu)} data-testid="account-button">
              <span className="avatar" aria-hidden="true">{initials(me.user.name)}</span>
              <span className="acct-name" data-testid="current-user">{me.user.short}<small>{me.user.role}</small></span><span aria-hidden="true">▾</span>
            </button>
            {menu && <AccountMenu me={me} onClose={() => setMenu(false)} onSignOut={onSignOut} onChangePw={() => { setMenu(false); setPw(true); }} />}
          </div>
        </div>
      </header>
      <main className="page">{children}</main>
      {pw && <ChangePasswordSheet onClose={() => setPw(false)} />}
      <Toasts />
    </div>
  );
}
